import React, { useState } from 'react';
import type { Offer, FlightSegment, RealFlightOption, Language } from '../../types';
import { resolveAirlineName, getAirlineNameByCode } from '../../utils/flightUtils';

// Helper: Format duration from minutes (e.g. "1375" or 1375) to "22h 55m"
export const formatDuration = (duration: string | number | undefined): string => {
    if (!duration && duration !== 0) return '';
    const val = typeof duration === 'string' ? duration.trim() : String(duration);
    // Already formatted (contains 'h' or 'm')
    if (/\d+h/.test(val) || /\d+m/.test(val)) return val;
    const mins = parseInt(val, 10);
    if (isNaN(mins) || mins <= 0) return val;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
};

// Helper: Format flight date/time for display
const formatFlightDateTime = (dateTimeStr: string | undefined, locale: string = 'sv-SE'): { date: string; time: string } => {
    if (!dateTimeStr) return { date: '', time: '' };
    try {
        const rawDate = dateTimeStr.includes('T') ? dateTimeStr.split('T')[0] : (dateTimeStr.includes(' ') ? dateTimeStr.split(' ')[0] : dateTimeStr);
        if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
            const [y, m, d] = rawDate.split('-').map(Number);
            const utcDate = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
            const timeMatch = dateTimeStr.includes('T') ? dateTimeStr.split('T')[1]?.match(/(\d{2}:\d{2})/) : dateTimeStr.match(/(\d{2}:\d{2})/);
            return {
                date: utcDate.toLocaleDateString(locale, { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' }),
                time: timeMatch ? timeMatch[1] : ''
            };
        }
        const date = new Date(dateTimeStr);
        if (isNaN(date.getTime())) {
            // Try to extract time from string like "07:40" or "2026-05-17T07:40:00"
            const timeMatch = dateTimeStr.match(/(\d{2}:\d{2})/);
            return { date: '', time: timeMatch ? timeMatch[1] : dateTimeStr };
        }
        return {
            date: date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }),
            time: date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false })
        };
    } catch (e) {
        return { date: '', time: '' };
    }
};

// Helper: Get airline name as string (handles object or string)
const getAirlineName = (airline: any): string => {
    if (!airline) return '';
    if (typeof airline === 'string') return airline;
    if (typeof airline === 'object' && airline.name) return airline.name;
    return '';
};

// Helper: Calculate layover duration between two segments
const calculateLayover = (arrivalTime: string, departureTime: string): string => {
    try {
        const arr = new Date(arrivalTime);
        const dep = new Date(departureTime);
        if (isNaN(arr.getTime()) || isNaN(dep.getTime())) return '';
        const diffMs = dep.getTime() - arr.getTime();
        if (diffMs <= 0) return '';
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
        if (hours > 0) return `${hours}h`;
        return `${mins}m`;
    } catch {
        return '';
    }
};

// Helper: Format layover duration from minutes
const formatLayoverFromMinutes = (minutes: number | undefined): string => {
    if (!minutes || minutes <= 0) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
};

// Helper: Get airline logo URL with fallback
const getAirlineLogoUrl = (segment: FlightSegment): string => {
    if (segment.logoUrl) return segment.logoUrl;
    const code = (segment.airlineCode || (segment.flightNumber ? segment.flightNumber.replace(/[^A-Za-z]/g, '').substring(0, 2) : '')).toUpperCase();
    if (code && code.length === 2) return `https://images.kiwi.com/airlines/64/${code}.png`;
    return 'https://cdn-icons-png.flaticon.com/512/723/723955.png';
};

// Helper: Get display airport name - prefers full name, falls back to code
const getAirportDisplay = (segment: FlightSegment, direction: 'departure' | 'arrival'): { name: string; code: string } => {
    if (direction === 'departure') {
        return {
            name: segment.departureAirportName || segment.departureAirport || '',
            code: segment.departureAirportCode || segment.departureAirport || ''
        };
    }
    return {
        name: segment.arrivalAirportName || segment.arrivalAirport || '',
        code: segment.arrivalAirportCode || segment.arrivalAirport || ''
    };
};

// =============================================
// SEGMENT ROW — Individual leg of a flight
// Shows: departure time → arrival time, airport codes, flight number, baggage
// =============================================
const SegmentDetailRow: React.FC<{
    segment: FlightSegment;
    language: Language;
    index: number;
}> = ({ segment, language, index }) => {
    const depAirport = getAirportDisplay(segment, 'departure');
    const arrAirport = getAirportDisplay(segment, 'arrival');
    const depTime = segment.departureTime || '';
    const arrTime = segment.arrivalTime || '';

    return (
        <div className="flex items-center gap-3 py-2.5 px-1">
            {/* Route indicator dot */}
            <div className="flex flex-col items-center flex-shrink-0 w-5">
                <div className="w-2.5 h-2.5 rounded-full bg-blue-500 border-2 border-blue-200"></div>
                <div className="w-0.5 flex-1 bg-blue-200 my-0.5 min-h-[16px]"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-blue-700 border-2 border-blue-300"></div>
            </div>

            {/* Flight Segment Details */}
            <div className="flex-1 min-w-0">
                {/* Departure */}
                <div className="flex items-center gap-2 mb-1">
                    {/* Airline logo small */}
                    <img
                        src={getAirlineLogoUrl(segment)}
                        alt={getAirlineName(segment.airline)}
                        className="w-4 h-4 object-contain rounded flex-shrink-0"
                        onError={(e) => { (e.target as HTMLImageElement).src = 'https://cdn-icons-png.flaticon.com/512/723/723955.png'; }}
                    />
                    <span className="text-sm font-black text-gray-900">{depTime}</span>
                    <span className="text-xs text-gray-400 font-medium">→</span>
                    <span className="text-sm font-black text-gray-900">{arrTime}</span>
                    <span className="text-[11px] text-gray-500 font-semibold ml-1">
                        {depAirport.code}{depAirport.code && arrAirport.code ? ' → ' : ''}{arrAirport.code}
                    </span>
                </div>
            </div>

            {/* Flight Number & Baggage */}
            <div className="flex items-center gap-3 flex-shrink-0">
                {segment.flightNumber && (
                    <span className="text-[11px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100">
                        {segment.flightNumber}
                    </span>
                )}
                {segment.baggage && (
                    <span className="text-[11px] text-gray-500 font-medium">
                        {segment.baggage}
                    </span>
                )}
            </div>
        </div>
    );
};

// =============================================
// LAYOVER INDICATOR — Between connecting segments
// Shows: layover duration and transfer airport
// =============================================
const LayoverIndicator: React.FC<{
    duration: string;
    airport: string;
    airportCode?: string;
    language: Language;
}> = ({ duration, airport, airportCode, language }) => (
    <div className="flex items-center gap-2 py-1 px-2 mx-6 my-0.5">
        <div className="flex-1 border-t border-dashed border-amber-300"></div>
        <div className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-3 py-1 rounded-full border border-amber-200">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-[11px] font-bold">
                {language === 'sv' ? 'Mellanlandning' : language === 'da' ? 'Mellemlanding' : 'Layover'} {airportCode || airport} • {duration}
            </span>
        </div>
        <div className="flex-1 border-t border-dashed border-amber-300"></div>
    </div>
);

// =============================================
// FLIGHT DIRECTION CARD — Outbound or Return
// Reference design: airline logo, flight number,
// full airport names, date, duration, stops, 
// expandable segment details
// =============================================
const FlightDirectionCard: React.FC<{
    label: string;
    icon: string;
    flight: FlightSegment;
    language: Language;
    colorScheme: 'sky' | 'indigo';
}> = ({ label, icon, flight, language, colorScheme }) => {
    const [showDetails, setShowDetails] = useState(false);
    const locale = language === 'sv' ? 'sv-SE' : language === 'da' ? 'da-DK' : 'en-US';
    const hasSegments = flight.segments && flight.segments.length > 1;

    const depAirport = getAirportDisplay(flight, 'departure');
    const arrAirport = getAirportDisplay(flight, 'arrival');

    // Color theme
    const theme = colorScheme === 'sky' ? {
        bg: 'bg-gradient-to-br from-sky-50 to-blue-50',
        border: 'border-sky-200',
        headerBg: 'bg-sky-100/70',
        accent: 'text-sky-700',
        accentBg: 'bg-sky-100 text-sky-700 border-sky-200',
        dateBg: 'bg-sky-600 text-white',
        lineColor: 'from-sky-400 to-blue-500',
        dotColor: 'bg-sky-500 border-sky-200',
        dotEndColor: 'bg-sky-700 border-sky-300',
    } : {
        bg: 'bg-gradient-to-br from-indigo-50 to-purple-50',
        border: 'border-indigo-200',
        headerBg: 'bg-indigo-100/70',
        accent: 'text-indigo-700',
        accentBg: 'bg-indigo-100 text-indigo-700 border-indigo-200',
        dateBg: 'bg-indigo-600 text-white',
        lineColor: 'from-indigo-400 to-purple-500',
        dotColor: 'bg-indigo-500 border-indigo-200',
        dotEndColor: 'bg-indigo-700 border-indigo-300',
    };

    return (
        <div className={`${theme.bg} rounded-2xl border ${theme.border} overflow-hidden shadow-sm`}>
            {/* ===== HEADER: Direction + Date ===== */}
            <div className={`flex items-center justify-between px-4 py-2.5 ${theme.headerBg}`}>
                <div className="flex items-center gap-2">
                    <span className="text-lg">{icon}</span>
                    <span className={`text-xs font-bold uppercase tracking-wider ${theme.accent}`}>{label}</span>
                </div>
                {flight.flightDate && (
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${theme.dateBg}`}>
                        {formatFlightDateTime(flight.flightDate, locale).date || flight.flightDate}
                    </span>
                )}
            </div>

            {/* ===== MAIN: Airline + Route Summary ===== */}
            <div className="px-4 py-4">
                <div className="flex items-start gap-4">
                    {/* Airline Logo (large) */}
                    <div className="flex flex-col items-center flex-shrink-0 min-w-[60px]">
                        <img
                            src={getAirlineLogoUrl(flight)}
                            alt={getAirlineName(flight.airline)}
                            className="w-10 h-10 object-contain rounded-lg bg-white p-1 shadow-sm border border-gray-100"
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://cdn-icons-png.flaticon.com/512/723/723955.png';
                            }}
                        />
                        {/* Flight Number (only if not generic Charter or FAKE) */}
                        {flight.flightNumber && 
                         flight.flightNumber.toLowerCase() !== 'charter' && 
                         !/^fake/i.test(flight.flightNumber) && (
                            <span className="text-[10px] font-bold text-gray-500 mt-1">{flight.flightNumber}</span>
                        )}
                        {/* Airline Name */}
                        {(() => {
                            const resolvedName = resolveAirlineName(flight.airline, flight.airlineCode, flight.flightNumber);
                            return resolvedName ? (
                                <span className="text-[10px] text-gray-500 font-semibold text-center leading-tight mt-0.5">
                                    {resolvedName}
                                </span>
                            ) : null;
                        })()}
                    </div>

                    {/* Route: Departure → Arrival */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        {/* Departure */}
                        <div className="text-left min-w-[60px]">
                            <div className="text-base font-bold text-gray-900 leading-none">{flight.departureTime || 'TBD'}</div>
                            <div className="text-[11px] font-semibold text-gray-600 mt-0.5">{depAirport.name || depAirport.code}</div>
                            {depAirport.name && depAirport.code && depAirport.name !== depAirport.code && (
                                <div className="text-[9px] text-gray-400 font-medium">{depAirport.code}</div>
                            )}
                        </div>

                        {/* Flight Path Visualization */}
                        <div className="flex-1 flex flex-col items-center px-1">
                            {/* Duration */}
                            <div className="text-[11px] text-gray-500 font-bold mb-1 bg-white/70 px-2 py-0.5 rounded-full">
                                {(() => {
                                    const dur = formatDuration(flight.duration);
                                    if (dur && dur !== '--') return dur;
                                    if (flight.departureTime && flight.arrivalTime) {
                                        try {
                                            const [dh, dm] = flight.departureTime.split(':').map(Number);
                                            const [ah, am] = flight.arrivalTime.split(':').map(Number);
                                            if (!isNaN(dh) && !isNaN(dm) && !isNaN(ah) && !isNaN(am)) {
                                                let depM = dh * 60 + dm;
                                                let arrM = ah * 60 + am;
                                                if (arrM < depM) arrM += 24 * 60;
                                                const diff = arrM - depM;
                                                if (diff >= 60 && diff <= 900) {
                                                    const h = Math.floor(diff / 60);
                                                    const m = diff % 60;
                                                    return m > 0 ? `${h}h ${m}m` : `${h}h`;
                                                }
                                            }
                                        } catch { }
                                    }
                                    return '4h 00m';
                                })()}
                            </div>
                            {/* Flight line */}
                            <div className="w-full flex items-center">
                                <div className={`w-2.5 h-2.5 rounded-full ${theme.dotColor} border-2 flex-shrink-0`}></div>
                                <div className={`flex-1 h-[2px] bg-gradient-to-r ${theme.lineColor}`}></div>
                                <svg className={`w-4 h-4 ${theme.accent} -ml-0.5 flex-shrink-0`} fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H1.35a.75.75 0 000 1.5h4.997l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                                </svg>
                            </div>
                            {/* Stops badge */}
                            <div className="mt-1">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${(flight.stops || 0) === 0
                                    ? 'bg-green-50 text-green-700 border-green-200'
                                    : 'bg-amber-50 text-amber-700 border-amber-200'
                                    }`}>
                                    {(flight.stops || 0) === 0
                                        ? (language === 'sv' ? 'Direkt' : language === 'da' ? 'Direkte' : 'Direct')
                                        : `${flight.stops} ${language === 'sv' ? 'stopp' : language === 'da' ? 'stop' : 'stop(s)'}`
                                    }
                                </span>
                            </div>
                        </div>

                        {/* Arrival */}
                        <div className="text-right min-w-[60px]">
                            <div className="text-base font-bold text-gray-900 leading-none">{flight.arrivalTime || 'TBD'}</div>
                            <div className="text-[11px] font-semibold text-gray-600 mt-0.5">{arrAirport.name || arrAirport.code}</div>
                            {arrAirport.name && arrAirport.code && arrAirport.name !== arrAirport.code && (
                                <div className="text-[9px] text-gray-400 font-medium">{arrAirport.code}</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ===== BAGGAGE ROW (Always displayed for package flights) ===== */}
                <div className="mt-3 pt-2 border-t border-gray-200/50 flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-1.5">
                        <span className="text-sm">💼</span>
                        <span className="text-[11px] text-gray-600 font-medium">
                            {language === 'sv' ? 'Incheckat bagage' : language === 'da' ? 'Indchecket bagage' : 'Checked baggage'}:
                            <span className="text-teal-600 font-bold ml-1">{flight.baggage || '20 kg'}</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="text-sm">🧳</span>
                        <span className="text-[11px] text-gray-600 font-medium">
                            {language === 'sv' ? 'Handbagage' : language === 'da' ? 'Håndbagage' : 'Hand luggage'}:
                            <span className="text-teal-600 font-bold ml-1">{language === 'sv' ? 'inkluderad (8 kg)' : language === 'da' ? 'inkluderet (8 kg)' : 'included (8 kg)'}</span>
                        </span>
                    </div>
                </div>
            </div>

            {/* ===== EXPANDABLE SEGMENT DETAILS ===== */}
            {hasSegments && (
                <>
                    {/* Toggle Button */}
                    <button
                        onClick={() => setShowDetails(!showDetails)}
                        className={`w-full flex items-center justify-center gap-2 py-2 border-t ${theme.border} hover:bg-white/50 transition-colors`}
                    >
                        <span className={`text-[11px] font-bold uppercase tracking-wider ${theme.accent}`}>
                            {language === 'sv' ? 'Resedetaljer' : language === 'da' ? 'Rejsedetaljer' : 'Travel Details'}
                        </span>
                        <svg
                            className={`w-3.5 h-3.5 ${theme.accent} transition-transform ${showDetails ? 'rotate-180' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {/* Segment Details (collapsible) */}
                    {showDetails && (
                        <div className="px-4 pb-4 bg-white/40">
                            {flight.segments!.map((seg, i) => (
                                <React.Fragment key={i}>
                                    <SegmentDetailRow
                                        segment={{
                                            ...seg,
                                            logoUrl: seg.logoUrl || flight.logoUrl,
                                            airline: seg.airline || flight.airline,
                                            baggage: seg.baggage || flight.baggage,
                                        }}
                                        language={language}
                                        index={i}
                                    />
                                    {/* Layover between segments */}
                                    {i < flight.segments!.length - 1 && (
                                        <LayoverIndicator
                                            duration={
                                                seg.layoverMinutes
                                                    ? formatLayoverFromMinutes(seg.layoverMinutes)
                                                    : calculateLayover(seg.arrivalTime, flight.segments![i + 1].departureTime)
                                            }
                                            airport={seg.layoverAirport || seg.arrivalAirport || ''}
                                            airportCode={seg.layoverAirportCode || seg.arrivalAirportCode || seg.arrivalAirport || ''}
                                            language={language}
                                        />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};


// =============================================
// FLIGHT DETAILS COMPACT — Main export component
// Used on hotel detail page to show flight info
// =============================================
export interface FlightDetailsCompactProps {
    offer: Offer;
    onChangeFlight?: () => void;
    lazyFlightData?: { outbound?: FlightSegment; return?: FlightSegment; loading: boolean };
    realOutbound?: RealFlightOption;
    realInbound?: RealFlightOption;
    onShowAlternativeFlights?: () => void;
    language: Language;
    onShowModal: (outbound: FlightSegment, returnFlight?: FlightSegment) => void;
}

export const FlightDetailsCompact: React.FC<FlightDetailsCompactProps> = ({
    offer, onChangeFlight, lazyFlightData, realOutbound, realInbound, onShowAlternativeFlights, language, onShowModal
}) => {
    // Prefer lazy-loaded data (getOfferDetails) over initial search data,
    // because initial search data often has only airport codes and empty times.
    // Lazy-loaded data has full airport names, formatted times, baggage, and segments.
    let outboundFlight = lazyFlightData?.outbound || offer.outboundFlight;
    let returnFlight = lazyFlightData?.return || offer.returnFlight;

    if (realOutbound) {
        outboundFlight = {
            departureTime: realOutbound.departureTime,
            arrivalTime: realOutbound.arrivalTime,
            departureAirport: realOutbound.departureAirport,
            arrivalAirport: realOutbound.arrivalAirport,
            airline: realOutbound.airline,
            airlineCode: realOutbound.airlineCode,
            flightDate: realOutbound.departureDate,
            logoUrl: realOutbound.logoUrl,
            duration: realOutbound.duration,
            flightNumber: realOutbound.flightNumber,
            stops: realOutbound.stops || 0,
            baggage: realOutbound.baggage || '20 kg',
            segments: realOutbound.segments as FlightSegment[] | undefined
        };
    }
    if (realInbound) {
        returnFlight = {
            departureTime: realInbound.departureTime,
            arrivalTime: realInbound.arrivalTime,
            departureAirport: realInbound.departureAirport,
            arrivalAirport: realInbound.arrivalAirport,
            airline: realInbound.airline,
            airlineCode: realInbound.airlineCode,
            flightDate: realInbound.departureDate,
            logoUrl: realInbound.logoUrl,
            duration: realInbound.duration,
            flightNumber: realInbound.flightNumber,
            stops: realInbound.stops || 0,
            baggage: realInbound.baggage || '20 kg',
            segments: realInbound.segments as FlightSegment[] | undefined
        };
    }

    // Ensure flight dates match the offer trip dates (checkIn & checkIn + night)
    const tripCheckIn = typeof offer?.checkIn === 'string' ? offer.checkIn.split('T')[0] : '';
    const tripNights = Number(offer?.night || (offer as any)?.nights || 7) || 7;
    let tripCheckOut = '';
    if (tripCheckIn && /^\d{4}-\d{2}-\d{2}$/.test(tripCheckIn)) {
        const [y, m, d] = tripCheckIn.split('-').map(Number);
        const co = new Date(Date.UTC(y, m - 1, d + tripNights));
        tripCheckOut = co.toISOString().split('T')[0];
    }

    if (outboundFlight && tripCheckIn) {
        outboundFlight = {
            ...outboundFlight,
            flightDate: tripCheckIn
        };
    }
    if (returnFlight && tripCheckOut) {
        returnFlight = {
            ...returnFlight,
            flightDate: tripCheckOut
        };
    }

    const isLoadingFlight = lazyFlightData?.loading;

    // Show loading state
    if (isLoadingFlight) {
        return (
            <div className="bg-gradient-to-r from-sky-50 to-blue-50 rounded-2xl p-5 my-3 border border-sky-100">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 animate-pulse">
                        <span className="text-2xl">✈️</span>
                    </div>
                    <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-40 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded animate-pulse w-56"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (!outboundFlight) {
        return (
            <div className="bg-gradient-to-r from-sky-50 to-blue-50 rounded-2xl p-5 my-3 border border-sky-100">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl">✈️</span>
                    </div>
                    <div className="flex-1">
                        <h4 className="text-sm font-bold text-gray-800">
                            {language === 'sv' ? 'Komplett Paket' : language === 'da' ? 'Komplet Pakke' : 'Complete Package'}
                        </h4>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {language === 'sv'
                                ? 'Inkluderar: Flyg, Hotell, Transfer & Skatter'
                                : language === 'da'
                                ? 'Inkluderer: Fly, Hotel, Transfer & Skatter'
                                : 'Includes: Flight, Hotel, Transfer & Taxes'}
                        </p>
                    </div>
                    <div className="text-right hidden sm:block">
                        <span className="text-[10px] font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                            {language === 'sv' ? 'Paketresa' : language === 'da' ? 'Pakkerejse' : 'Package Deal'}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="my-3 space-y-3">
            {/* Outbound Flight Card */}
            <FlightDirectionCard
                label={language === 'sv' ? 'Utresa' : language === 'da' ? 'Udrejse' : 'Outbound'}
                icon="🛫"
                flight={outboundFlight!}
                language={language}
                colorScheme="sky"
            />

            {/* Return Flight Card */}
            {returnFlight && (
                <FlightDirectionCard
                    label={language === 'sv' ? 'Hemresa' : language === 'da' ? 'Hjemrejse' : 'Return'}
                    icon="🛬"
                    flight={returnFlight}
                    language={language}
                    colorScheme="indigo"
                />
            )}

            {/* Change Flight Button */}
            {onShowAlternativeFlights && (
                <div className="text-center pt-1">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onShowAlternativeFlights();
                        }}
                        className="inline-flex items-center gap-2 text-xs font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-full border border-blue-200 transition-all"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                        </svg>
                        {language === 'sv' ? 'Ändra flyg' : language === 'da' ? 'Skift fly' : 'Change flight'}
                    </button>
                </div>
            )}
        </div>
    );
};
